import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiRefreshCw,
  FiDollarSign,
  FiBriefcase,
  FiUsers,
  FiTrendingUp,
  FiAlertCircle,
  FiCalendar,
  FiUserPlus,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { formatAUD } from '../../../utils/formatCurrency';
import { useAdminPageSearch } from '../../../utils/useAdminPageSearch';
import '../styles/WorkforceForecast.css';

const USERS_QUERY = `
  query ForecastUsers($page: Int, $limit: Int) {
    getAllUser(page: $page, limit: $limit) {
      data {
        id
        firstName
        lastName
        email
        roles
        jobTitle
        typeOfWork
        employmentType
        isActive
        perHourRate
        salaryTerm
        payType
        joiningDate
        branchId
      }
      totalPages
    }
  }
`;

const PROJECTS_QUERY = `
  query ForecastProjects($limit: Int) {
    findAllProject(limit: $limit) {
      id
      name
      projectId
      status
      startDate
      deadLine
      startTime
      endTime
      quotedTotal
      recurrenceFrequency
      nextVisitAt
      booking { id fullName address }
      manager { id firstName lastName }
    }
  }
`;

const ROSTER_QUERY = `
  query ForecastRosterTasks {
    getAdminRosterTasks {
      id
      title
      date
      time
      status
      user { id firstName lastName }
      project {
        id
        name
        startDate
        startTime
        endTime
        booking { fullName }
      }
    }
  }
`;

const LEAVE_QUERY = `
  query ForecastLeave {
    leaveRequests {
      id
      name
      employeeId
      leaveType
      startDate
      endDate
      status
      duration
    }
  }
`;

const OFFERS_QUERY = `
  query ForecastOffers {
    recruitmentJobOffers {
      id
      name
      role
      salary
      status
      offerDate
      expiresAt
      branch
    }
  }
`;

const CONTRACTORS_QUERY = `
  query ForecastContractors {
    getAllContractorAssignments {
      id
      status
      price
      contractorName
      createdAt
      project {
        id
        name
        startDate
        deadLine
      }
    }
  }
`;

function parseDate(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function monthLabel(d) {
  return d.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
}

function shortMonth(d) {
  return d.toLocaleDateString('en-AU', { month: 'short' });
}

/** Next 3 full calendar months (not including current month). */
function getForecastMonths(from = new Date()) {
  const months = [];
  for (let i = 1; i <= 3; i += 1) {
    const anchor = new Date(from.getFullYear(), from.getMonth() + i, 1);
    months.push({
      index: i,
      key: `${anchor.getFullYear()}-${String(anchor.getMonth() + 1).padStart(2, '0')}`,
      label: monthLabel(anchor),
      short: shortMonth(anchor),
      start: startOfMonth(anchor),
      end: endOfMonth(anchor),
    });
  }
  return months;
}

function overlapsMonth(start, end, monthStart, monthEnd) {
  const a = start || end;
  const b = end || start;
  if (!a && !b) return false;
  const s = a || b;
  const e = b || a;
  return s <= monthEnd && e >= monthStart;
}

function parseMinutes(time) {
  const m = String(time || '').match(/(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

function hoursFromTimes(startTime, endTime, timeRange) {
  const range = String(timeRange || '');
  const rm = range.match(/(\d{1,2}):(\d{2})\s*[-–]\s*(\d{1,2}):(\d{2})/);
  if (rm) {
    const a = Number(rm[1]) * 60 + Number(rm[2]);
    const b = Number(rm[3]) * 60 + Number(rm[4]);
    const mins = b >= a ? b - a : b + 24 * 60 - a;
    return mins / 60;
  }
  const a = parseMinutes(startTime);
  const b = parseMinutes(endTime);
  if (a != null && b != null) {
    const mins = b >= a ? b - a : b + 24 * 60 - a;
    return mins / 60;
  }
  return 4;
}

function isActiveStaff(u) {
  if (u?.isActive === false) return false;
  const roles = (Array.isArray(u?.roles) ? u.roles : [u?.roles])
    .map((r) => String(r || '').toLowerCase())
    .join(' ');
  // Prefer field / ops roles for labor; still count anyone with a rate
  if (Number(u?.perHourRate) > 0) return true;
  return /cleaner|manager|supervisor|staff|employee|field|ops/.test(
    `${roles} ${u?.jobTitle || ''} ${u?.typeOfWork || ''}`,
  );
}

function isFieldCleaner(u) {
  const hay = `${(u?.roles || []).join(' ')} ${u?.jobTitle || ''} ${u?.typeOfWork || ''}`.toLowerCase();
  return /cleaner|field|ops|supervisor/.test(hay);
}

function estimateMonthlyPay(u) {
  const rate = Number(u?.perHourRate) || 0;
  if (!rate) return 0;
  const term = `${u?.salaryTerm || ''} ${u?.payType || ''} ${u?.employmentType || ''}`.toLowerCase();
  if (term.includes('annual') || term.includes('year') || (term.includes('salary') && rate > 500)) {
    return rate > 500 ? rate / 12 : rate * 38 * (52 / 12);
  }
  if (term.includes('casual') || term.includes('part')) return rate * 80;
  return rate * 152;
}

function parseSalaryText(text) {
  if (!text) return 0;
  const nums = String(text).replace(/,/g, '').match(/(\d+(\.\d+)?)/g);
  if (!nums?.length) return 0;
  const n = Number(nums[0]);
  if (!Number.isFinite(n)) return 0;
  if (n > 500) return n / 12; // treat as annual
  return n * 152; // treat as hourly → month
}

function recurrenceVisitsPerMonth(freq) {
  const f = String(freq || '').toLowerCase();
  if (!f || f.includes('once') || f.includes('one')) return 1;
  if (f.includes('day')) return 20;
  if (f.includes('week') && f.includes('2')) return 8;
  if (f.includes('fortnight') || f.includes('bi')) return 2;
  if (f.includes('week')) return 4;
  if (f.includes('month')) return 1;
  if (f.includes('quarter')) return 0.33;
  return 1;
}

function fullName(u) {
  return `${u?.firstName || ''} ${u?.lastName || ''}`.trim();
}

function statusOk(status) {
  const s = String(status || '').toLowerCase();
  return !['cancel', 'cancelled', 'rejected', 'declined', 'completed', 'terminated'].includes(s);
}

async function loadAllUsers() {
  const first = await graphqlRequest(USERS_QUERY, { page: 1, limit: 100 });
  const page1 = Array.isArray(first?.getAllUser?.data) ? first.getAllUser.data : [];
  const totalPages = Math.max(1, Number(first?.getAllUser?.totalPages) || 1);
  if (totalPages === 1) return page1;
  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) =>
      graphqlRequest(USERS_QUERY, { page: i + 2, limit: 100 }),
    ),
  );
  return [
    ...page1,
    ...rest.flatMap((r) =>
      Array.isArray(r?.getAllUser?.data) ? r.getAllUser.data : [],
    ),
  ];
}

export default function WorkforceForecast() {
  const { get, patch } = useAdminPageSearch();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [leave, setLeave] = useState([]);
  const [offers, setOffers] = useState([]);
  const [contractors, setContractors] = useState([]);

  const months = useMemo(() => getForecastMonths(new Date()), []);
  const urlTab = get('tab');
  const selectedMonthKey = months.some((m) => m.key === urlTab)
    ? urlTab
    : months[0]?.key || null;

  const load = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const [userList, projRes, rosterRes, leaveRes, offerRes, contractorRes] =
        await Promise.all([
          loadAllUsers().catch(() => []),
          graphqlRequest(PROJECTS_QUERY, { limit: 400 }).catch(() => ({})),
          graphqlRequest(ROSTER_QUERY).catch(() => ({})),
          graphqlRequest(LEAVE_QUERY).catch(() => ({})),
          graphqlRequest(OFFERS_QUERY).catch(() => ({})),
          graphqlRequest(CONTRACTORS_QUERY).catch(() => ({})),
        ]);

      setUsers(Array.isArray(userList) ? userList : []);
      setProjects(
        Array.isArray(projRes?.findAllProject) ? projRes.findAllProject : [],
      );
      setTasks(
        Array.isArray(rosterRes?.getAdminRosterTasks)
          ? rosterRes.getAdminRosterTasks
          : [],
      );
      setLeave(
        Array.isArray(leaveRes?.leaveRequests) ? leaveRes.leaveRequests : [],
      );
      setOffers(
        Array.isArray(offerRes?.recruitmentJobOffers)
          ? offerRes.recruitmentJobOffers
          : [],
      );
      setContractors(
        Array.isArray(contractorRes?.getAllContractorAssignments)
          ? contractorRes.getAllContractorAssignments
          : [],
      );
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Failed to load forecast');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const activeStaff = useMemo(
    () => (users || []).filter(isActiveStaff),
    [users],
  );
  const cleaners = useMemo(
    () => activeStaff.filter(isFieldCleaner),
    [activeStaff],
  );

  const blendedHourly = useMemo(() => {
    const rates = activeStaff
      .map((u) => Number(u.perHourRate) || 0)
      .filter((r) => r > 0 && r < 500);
    if (!rates.length) return 35; // AU cleaning industry fallback
    return rates.reduce((a, b) => a + b, 0) / rates.length;
  }, [activeStaff]);

  const baselinePayroll = useMemo(
    () => activeStaff.reduce((sum, u) => sum + estimateMonthlyPay(u), 0),
    [activeStaff],
  );

  const monthForecasts = useMemo(() => {
    return months.map((m) => {
      const monthProjects = (projects || []).filter((p) => {
        if (!statusOk(p.status)) return false;
        const start = parseDate(p.startDate);
        const end = parseDate(p.deadLine) || parseDate(p.nextVisitAt);
        if (overlapsMonth(start, end, m.start, m.end)) return true;
        const next = parseDate(p.nextVisitAt);
        if (next && next >= m.start && next <= m.end) return true;
        return false;
      });

      let scheduledHours = 0;
      let jobVisits = 0;
      let quotedRevenue = 0;

      monthProjects.forEach((p) => {
        const hoursOne = hoursFromTimes(p.startTime, p.endTime);
        const visits = recurrenceVisitsPerMonth(p.recurrenceFrequency);
        const start = parseDate(p.startDate);
        // If project only spans part of month, scale visits roughly
        let scale = 1;
        if (start && start > m.start) {
          const daysLeft =
            (m.end - start) / (1000 * 60 * 60 * 24) + 1;
          const daysInMonth = m.end.getDate();
          scale = Math.max(0.25, Math.min(1, daysLeft / daysInMonth));
        }
        const v = Math.max(1, Math.round(visits * scale));
        jobVisits += v;
        scheduledHours += hoursOne * v;
        quotedRevenue += (Number(p.quotedTotal) || 0) * (visits > 1 ? scale : 1);
      });

      const monthTasks = (tasks || []).filter((t) => {
        if (!statusOk(t.status)) return false;
        const d = parseDate(t.date) || parseDate(t.project?.startDate);
        return d && d >= m.start && d <= m.end;
      });

      monthTasks.forEach((t) => {
        scheduledHours += hoursFromTimes(
          t.project?.startTime,
          t.project?.endTime,
          t.time,
        );
      });

      // Avoid double-count: if tasks exist for month, prefer task hours for labor when larger
      const taskHours = monthTasks.reduce(
        (sum, t) =>
          sum +
          hoursFromTimes(t.project?.startTime, t.project?.endTime, t.time),
        0,
      );

      const workHours = Math.max(scheduledHours, taskHours);
      // Capacity: cleaners × ~152h; leave reduces
      const approvedLeave = (leave || []).filter((l) => {
        if (String(l.status || '').toLowerCase() !== 'approved') return false;
        const s = parseDate(l.startDate);
        const e = parseDate(l.endDate) || s;
        return overlapsMonth(s, e, m.start, m.end);
      });
      const leaveDays = approvedLeave.reduce((sum, l) => {
        const s = parseDate(l.startDate);
        const e = parseDate(l.endDate) || s;
        if (!s) return sum;
        const ms = Math.max(s, m.start);
        const me = Math.min(e, m.end);
        const days = Math.max(1, Math.round((me - ms) / (86400000)) + 1);
        return sum + days;
      }, 0);
      const leaveHours = leaveDays * 7.6;
      const capacityHours = Math.max(0, cleaners.length * 152 - leaveHours);
      const gapHours = Math.max(0, workHours - capacityHours);
      const staffNeededExtra = gapHours > 0 ? Math.ceil(gapHours / 152) : 0;

      const rosterLaborCost = workHours * blendedHourly;
      const forecastSalary = Math.max(baselinePayroll, rosterLaborCost * 0.85 + baselinePayroll * 0.15);

      const monthContractors = (contractors || []).filter((c) => {
        if (String(c.status || '').toLowerCase() === 'declined') return false;
        const d =
          parseDate(c.project?.startDate) ||
          parseDate(c.project?.deadLine) ||
          parseDate(c.createdAt);
        return d && d >= m.start && d <= m.end;
      });
      const contractorCost = monthContractors.reduce(
        (sum, c) => sum + (Number(c.price) || 0),
        0,
      );

      const pipelineOffers = (offers || []).filter((o) => {
        const st = String(o.status || '').toLowerCase();
        return st === 'pending' || st === 'accepted' || st === 'sent';
      });
      const hireCost = pipelineOffers
        .filter((o) => String(o.status || '').toLowerCase() === 'accepted')
        .reduce((sum, o) => sum + parseSalaryText(o.salary), 0);

      return {
        ...m,
        monthProjects,
        monthTasks,
        jobVisits,
        workHours: Math.round(workHours * 10) / 10,
        capacityHours: Math.round(capacityHours * 10) / 10,
        gapHours: Math.round(gapHours * 10) / 10,
        staffNeededExtra,
        leaveCount: approvedLeave.length,
        leaveDays,
        quotedRevenue,
        rosterLaborCost,
        forecastSalary: forecastSalary + hireCost * 0.3,
        contractorCost,
        contractorCount: monthContractors.length,
        pipelineOffers,
        utilisation:
          capacityHours > 0
            ? Math.min(150, Math.round((workHours / capacityHours) * 100))
            : workHours > 0
              ? 100
              : 0,
      };
    });
  }, [
    months,
    projects,
    tasks,
    leave,
    cleaners.length,
    blendedHourly,
    baselinePayroll,
    contractors,
    offers,
  ]);

  const selected = useMemo(
    () =>
      monthForecasts.find((m) => m.key === selectedMonthKey) ||
      monthForecasts[0],
    [monthForecasts, selectedMonthKey],
  );

  const totals = useMemo(() => {
    return monthForecasts.reduce(
      (acc, m) => ({
        salary: acc.salary + m.forecastSalary,
        hours: acc.hours + m.workHours,
        jobs: acc.jobs + m.jobVisits,
        revenue: acc.revenue + m.quotedRevenue,
        contractors: acc.contractors + m.contractorCost,
      }),
      { salary: 0, hours: 0, jobs: 0, revenue: 0, contractors: 0 },
    );
  }, [monthForecasts]);

  const maxBar = useMemo(() => {
    const vals = monthForecasts.flatMap((m) => [
      m.forecastSalary,
      m.workHours * blendedHourly,
      m.quotedRevenue,
    ]);
    return Math.max(1, ...vals);
  }, [monthForecasts, blendedHourly]);

  const horizonLabel = useMemo(() => {
    if (months.length < 3) return 'Next 3 months';
    return `${months[0].short} – ${months[2].label}`;
  }, [months]);

  return (
    <div className="wf-page">
      <header className="wf-header">
        <div>
          <h1>Forecasting</h1>
          <p>
            {horizonLabel} · Salary, scheduled work, capacity and hiring built
            from live SAS projects, rosters, staff rates, leave and offers.
          </p>
        </div>
        <button
          type="button"
          className="wf-btn wf-btn--primary"
          onClick={() => void load(true)}
          disabled={loading || refreshing}
        >
          <FiRefreshCw className={refreshing ? 'is-spin' : ''} /> Refresh
        </button>
      </header>

      <div className="wf-howto">
        <strong>How this forecast works</strong>
        <ul>
          <li>
            <b>Work</b> — projects &amp; roster tasks dated in each future month
            (recurring jobs counted by frequency).
          </li>
          <li>
            <b>Salary</b> — staff <code>perHourRate</code> × typical monthly
            hours, lifted when rostered work needs more labour.
          </li>
          <li>
            <b>Capacity</b> — field cleaners available hours minus approved leave;
            shows extra headcount if work exceeds capacity.
          </li>
          <li>
            <b>Hiring</b> — open / accepted job offers and their salary impact.
          </li>
        </ul>
      </div>

      {loading ? (
        <div className="wf-empty">Building 3-month forecast…</div>
      ) : (
        <>
          <div className="wf-stats">
            <div className="wf-stat wf-stat--blue">
              <div className="wf-stat-icon">
                <FiDollarSign />
              </div>
              <div>
                <div className="wf-stat-value">{formatAUD(totals.salary)}</div>
                <div className="wf-stat-label">3-mo salary forecast</div>
              </div>
            </div>
            <div className="wf-stat wf-stat--green">
              <div className="wf-stat-icon">
                <FiBriefcase />
              </div>
              <div>
                <div className="wf-stat-value">{totals.jobs}</div>
                <div className="wf-stat-label">Forecast job visits</div>
              </div>
            </div>
            <div className="wf-stat wf-stat--amber">
              <div className="wf-stat-icon">
                <FiTrendingUp />
              </div>
              <div>
                <div className="wf-stat-value">
                  {Math.round(totals.hours)}h
                </div>
                <div className="wf-stat-label">Scheduled work hours</div>
              </div>
            </div>
            <div className="wf-stat wf-stat--slate">
              <div className="wf-stat-icon">
                <FiUsers />
              </div>
              <div>
                <div className="wf-stat-value">
                  {cleaners.length}
                  <span className="wf-stat-sub"> / {activeStaff.length}</span>
                </div>
                <div className="wf-stat-label">Cleaners / active staff</div>
              </div>
            </div>
          </div>

          <div className="wf-month-tabs">
            {monthForecasts.map((m) => (
              <button
                key={m.key}
                type="button"
                className={`wf-month-tab ${
                  selected?.key === m.key ? 'is-active' : ''
                }`}
                onClick={() => patch({ tab: m.key })}
              >
                <span className="wf-month-tab-label">{m.short}</span>
                <span className="wf-month-tab-salary">
                  {formatAUD(m.forecastSalary)}
                </span>
                <span className="wf-month-tab-meta">
                  {m.jobVisits} jobs · {m.workHours}h
                </span>
              </button>
            ))}
          </div>

          <div className="wf-chart-card">
            <div className="wf-chart-head">
              <h2>3-month outlook</h2>
              <span>Salary vs work-driven labour vs quoted work</span>
            </div>
            <div className="wf-chart">
              {monthForecasts.map((m) => (
                <div key={m.key} className="wf-chart-col">
                  <div className="wf-bars">
                    <div
                      className="wf-bar wf-bar--salary"
                      style={{
                        height: `${Math.max(
                          8,
                          (m.forecastSalary / maxBar) * 100,
                        )}%`,
                      }}
                      title={`Salary ${formatAUD(m.forecastSalary)}`}
                    />
                    <div
                      className="wf-bar wf-bar--labor"
                      style={{
                        height: `${Math.max(
                          8,
                          ((m.workHours * blendedHourly) / maxBar) * 100,
                        )}%`,
                      }}
                      title={`Roster labour ${formatAUD(
                        m.workHours * blendedHourly,
                      )}`}
                    />
                    <div
                      className="wf-bar wf-bar--revenue"
                      style={{
                        height: `${Math.max(
                          8,
                          (m.quotedRevenue / maxBar) * 100,
                        )}%`,
                      }}
                      title={`Quoted ${formatAUD(m.quotedRevenue)}`}
                    />
                  </div>
                  <div className="wf-chart-label">{m.short}</div>
                </div>
              ))}
            </div>
            <div className="wf-legend">
              <span>
                <i className="wf-dot wf-dot--salary" /> Salary forecast
              </span>
              <span>
                <i className="wf-dot wf-dot--labor" /> Roster labour
              </span>
              <span>
                <i className="wf-dot wf-dot--revenue" /> Quoted jobs
              </span>
            </div>
          </div>

          {selected ? (
            <div className="wf-detail-grid">
              <section className="wf-card">
                <div className="wf-card-head">
                  <FiDollarSign />
                  <div>
                    <h3>Salary & labour — {selected.short}</h3>
                    <p>Baseline payroll + scheduled work pressure</p>
                  </div>
                </div>
                <div className="wf-kpi-row">
                  <div>
                    <span className="wf-k">Forecast salary cost</span>
                    <strong>{formatAUD(selected.forecastSalary)}</strong>
                  </div>
                  <div>
                    <span className="wf-k">Baseline (rates)</span>
                    <strong>{formatAUD(baselinePayroll)}</strong>
                  </div>
                  <div>
                    <span className="wf-k">Roster-driven labour</span>
                    <strong>
                      {formatAUD(selected.workHours * blendedHourly)}
                    </strong>
                  </div>
                  <div>
                    <span className="wf-k">Blended hourly</span>
                    <strong>{formatAUD(blendedHourly)}</strong>
                  </div>
                  <div>
                    <span className="wf-k">Contractor cost</span>
                    <strong>{formatAUD(selected.contractorCost)}</strong>
                  </div>
                </div>
                <p className="wf-note">
                  Uses each employee&apos;s <b>per hour rate</b> from Team /
                  Employee Directory. When rostered hours exceed quiet-month
                  baseline, forecast salary rises.
                </p>
              </section>

              <section className="wf-card">
                <div className="wf-card-head">
                  <FiBriefcase />
                  <div>
                    <h3>Work volume — {selected.short}</h3>
                    <p>Projects & tasks in this month</p>
                  </div>
                </div>
                <div className="wf-kpi-row">
                  <div>
                    <span className="wf-k">Job visits</span>
                    <strong>{selected.jobVisits}</strong>
                  </div>
                  <div>
                    <span className="wf-k">Work hours</span>
                    <strong>{selected.workHours}h</strong>
                  </div>
                  <div>
                    <span className="wf-k">Projects touching month</span>
                    <strong>{selected.monthProjects.length}</strong>
                  </div>
                  <div>
                    <span className="wf-k">Roster tasks</span>
                    <strong>{selected.monthTasks.length}</strong>
                  </div>
                  <div>
                    <span className="wf-k">Quoted value</span>
                    <strong>{formatAUD(selected.quotedRevenue)}</strong>
                  </div>
                </div>
                <div className="wf-list">
                  {selected.monthProjects.slice(0, 6).map((p) => (
                    <div key={p.id} className="wf-list-row">
                      <div>
                        <strong>{p.name || 'Untitled project'}</strong>
                        <div className="wf-muted">
                          {p.booking?.fullName || '—'} ·{' '}
                          {p.recurrenceFrequency || 'one-off'}
                        </div>
                      </div>
                      <div className="wf-list-right">
                        {formatAUD(p.quotedTotal)}
                      </div>
                    </div>
                  ))}
                  {selected.monthProjects.length === 0 ? (
                    <div className="wf-muted">
                      No dated projects in this month yet — salary still uses
                      staff rates; add project dates to refine work hours.
                    </div>
                  ) : null}
                </div>
              </section>

              <section className="wf-card">
                <div className="wf-card-head">
                  <FiUsers />
                  <div>
                    <h3>Capacity & leave — {selected.short}</h3>
                    <p>Can the team cover the work?</p>
                  </div>
                </div>
                <div className="wf-util">
                  <div className="wf-util-track">
                    <div
                      className={`wf-util-fill ${
                        selected.utilisation > 100
                          ? 'is-over'
                          : selected.utilisation > 85
                            ? 'is-high'
                            : ''
                      }`}
                      style={{
                        width: `${Math.min(100, selected.utilisation)}%`,
                      }}
                    />
                  </div>
                  <div className="wf-util-label">
                    {selected.utilisation}% utilisation
                  </div>
                </div>
                <div className="wf-kpi-row">
                  <div>
                    <span className="wf-k">Capacity hours</span>
                    <strong>{selected.capacityHours}h</strong>
                  </div>
                  <div>
                    <span className="wf-k">Leave days</span>
                    <strong>{selected.leaveDays}</strong>
                  </div>
                  <div>
                    <span className="wf-k">Hours gap</span>
                    <strong
                      className={selected.gapHours > 0 ? 'is-alert' : ''}
                    >
                      {selected.gapHours}h
                    </strong>
                  </div>
                  <div>
                    <span className="wf-k">Extra staff needed</span>
                    <strong
                      className={
                        selected.staffNeededExtra > 0 ? 'is-alert' : ''
                      }
                    >
                      {selected.staffNeededExtra}
                    </strong>
                  </div>
                </div>
                {selected.gapHours > 0 ? (
                  <div className="wf-banner">
                    <FiAlertCircle /> Work exceeds capacity by{' '}
                    {selected.gapHours}h — plan overtime, contractors, or hire{' '}
                    {selected.staffNeededExtra} cleaner
                    {selected.staffNeededExtra === 1 ? '' : 's'}.
                  </div>
                ) : (
                  <div className="wf-banner wf-banner--ok">
                    Capacity covers forecast work for {selected.short}.
                  </div>
                )}
              </section>

              <section className="wf-card">
                <div className="wf-card-head">
                  <FiUserPlus />
                  <div>
                    <h3>Hiring pipeline</h3>
                    <p>Offers that may add salary in the next months</p>
                  </div>
                </div>
                <div className="wf-list">
                  {(offers || [])
                    .filter((o) => {
                      const st = String(o.status || '').toLowerCase();
                      return (
                        st === 'pending' ||
                        st === 'accepted' ||
                        st === 'sent'
                      );
                    })
                    .slice(0, 8)
                    .map((o) => (
                      <div key={o.id} className="wf-list-row">
                        <div>
                          <strong>{o.name}</strong>
                          <div className="wf-muted">
                            {o.role} · {o.status}
                            {o.branch ? ` · ${o.branch}` : ''}
                          </div>
                        </div>
                        <div className="wf-list-right">
                          {o.salary || '—'}
                        </div>
                      </div>
                    ))}
                  {(offers || []).filter((o) => {
                    const st = String(o.status || '').toLowerCase();
                    return (
                      st === 'pending' || st === 'accepted' || st === 'sent'
                    );
                  }).length === 0 ? (
                    <div className="wf-muted">
                      No open job offers. Recruitment → Job Offers will appear
                      here.
                    </div>
                  ) : null}
                </div>
              </section>
            </div>
          ) : null}

          <section className="wf-table-card">
            <div className="wf-card-head">
              <FiCalendar />
              <div>
                <h3>Month-by-month summary</h3>
                <p>Export-ready view of the next quarter</p>
              </div>
            </div>
            <div className="wf-table-wrap">
              <table className="wf-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Salary forecast</th>
                    <th>Work hours</th>
                    <th>Job visits</th>
                    <th>Utilisation</th>
                    <th>Gap / hires</th>
                    <th>Quoted work</th>
                    <th>Contractors</th>
                  </tr>
                </thead>
                <tbody>
                  {monthForecasts.map((m) => (
                    <tr
                      key={m.key}
                      className={
                        selected?.key === m.key ? 'is-selected' : ''
                      }
                      onClick={() => patch({ tab: m.key })}
                    >
                      <td>
                        <strong>{m.label}</strong>
                      </td>
                      <td>{formatAUD(m.forecastSalary)}</td>
                      <td>{m.workHours}h</td>
                      <td>{m.jobVisits}</td>
                      <td>
                        <span
                          className={`wf-pill ${
                            m.utilisation > 100
                              ? 'wf-pill--danger'
                              : m.utilisation > 85
                                ? 'wf-pill--warn'
                                : 'wf-pill--good'
                          }`}
                        >
                          {m.utilisation}%
                        </span>
                      </td>
                      <td>
                        {m.gapHours > 0
                          ? `${m.gapHours}h / +${m.staffNeededExtra}`
                          : '—'}
                      </td>
                      <td>{formatAUD(m.quotedRevenue)}</td>
                      <td>{formatAUD(m.contractorCost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
